import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Megaphone, Users, Eye, TrendingUp, CheckCircle, Loader2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DevAdSection } from '../components/DevAd';

const BG_IMAGE = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";

export const AdvertisePage = () => {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    ad_type: 'homepage_banner',
    message: '',
    budget_range: '',
  });

  // Pre-select ad type from URL parameter (e.g., /advertise?type=homepage_banner)
  useEffect(() => {
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl) {
      setFormData(prev => ({ ...prev, ad_type: typeFromUrl }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.createAdInquiry(formData);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully! We will contact you soon.');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: Users,
      title: 'Reach IRE Teachers Nationwide',
      description: 'Connect with Islamic Religious Education teachers across all 47 counties in Kenya.',
    },
    {
      icon: Eye,
      title: 'High Visibility',
      description: 'Your brand featured prominently on our homepage carousel and resource pages.',
    },
    {
      icon: TrendingUp,
      title: 'Targeted Audience',
      description: 'Reach a dedicated community of educators invested in Islamic education.',
    },
  ];

  const adTypes = [
    {
      value: 'homepage_banner',
      label: 'Homepage Banner',
      description: 'Featured in our rotating homepage carousel',
      price: 'Custom Pricing/ Month',
    },
    {
      value: 'sponsored_resource',
      label: 'Sponsored Resource',
      description: 'Your resource featured at the top of listings',
      price: 'Custom Pricing/ Month',
    },
    {
      value: 'newsletter_feature',
      label: 'Newsletter Feature',
      description: 'Featured in our monthly teacher newsletter',
      price: 'Custom Pricing/ Issue',
    },
    {
      value: 'event_sponsorship',
      label: 'Event Sponsorship',
      description: 'Sponsor AKIT workshops and seminars',
      price: 'Custom pricing',
    },
  ];

  const guidelines = [
    'All advertisements must be Islamically permissible (Halal)',
    'No promotion of interest-based (Riba) financial products',
    'No content promoting gambling, alcohol, or prohibited substances',
    'Educational and family-friendly content only',
    'Products/services must align with Islamic values',
    'AKIT reserves the right to reject any unlawful advertisement',
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Hero Section */}
      <section 
        className="relative py-20"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-6">
            <Megaphone className="h-5 w-5" />
            <span className="text-sm font-medium">Advertising Opportunities</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Advertise with <span className="text-primary">AKIT</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Reach thousands of Islamic Religious Education teachers across Kenya with your 
            Halal products and services.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl -mt-10">
        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="glass-card-heavy p-6 rounded-2xl text-center"
              data-testid={`benefit-${index}`}
            >
              <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <benefit.icon className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground mb-2">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Ad Types & Guidelines */}
          <div className="space-y-8">
            {/* Advertising Options */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Advertising Options
              </h2>
              <div className="space-y-4">
                {adTypes.map((type, index) => (
                  <div 
                    key={index}
                    className="glass-card p-5 rounded-xl hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading font-bold text-foreground">{type.label}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
                      </div>
                      <span className="text-primary font-bold text-sm whitespace-nowrap">{type.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Halal Guidelines */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Halal Advertising Guidelines
              </h2>
              <div className="glass-card p-6 rounded-xl border-primary/30">
                <p className="text-primary font-amiri text-lg mb-4 text-center">
                  إِنَّ اللَّهَ طَيِّبٌ لَا يَقْبَلُ إِلَّا طَيِّبًا
                </p>
                <p className="text-xs text-muted-foreground text-center mb-6">
                  "Indeed, Allah is Good and accepts only that which is good."
                </p>
                <ul className="space-y-3">
                  {guidelines.map((guideline, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{guideline}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Inquiry Form */}
          <div>
            <div className="glass-card-heavy rounded-2xl p-8 sticky top-24">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="h-16 w-16 rounded-full bg-primary/20 mx-auto flex items-center justify-center mb-6">
                    <CheckCircle className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
                    Inquiry Received!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    JazakAllah Khair for your interest. Our team will review your inquiry and 
                    contact you within 2-3 business days.
                  </p>
                  <Button 
                    onClick={() => { setSubmitted(false); setFormData({ business_name: '', contact_name: '', email: '', phone: '', ad_type: 'homepage_banner', message: '', budget_range: '' }); }}
                    className="btn-primary"
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                    Get Started
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Fill out the form below and we'll get back to you with advertising options.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground">Business Name</label>
                        <Input
                          name="business_name"
                          value={formData.business_name}
                          onChange={handleChange}
                          className="input-glass"
                          placeholder="Your Business Name"
                          required
                          data-testid="ad-business-name"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground">Contact Name</label>
                        <Input
                          name="contact_name"
                          value={formData.contact_name}
                          onChange={handleChange}
                          className="input-glass"
                          placeholder="Your Name"
                          required
                          data-testid="ad-contact-name"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground">Email Address</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="input-glass"
                          placeholder="email@business.com"
                          required
                          data-testid="ad-email"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-muted-foreground">Phone Number</label>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="input-glass"
                          placeholder="+254 7XX XXX XXX"
                          data-testid="ad-phone"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-muted-foreground">Advertising Type</label>
                      <select
                        name="ad_type"
                        value={formData.ad_type}
                        onChange={handleChange}
                        className="input-glass w-full h-12"
                        data-testid="ad-type"
                      >
                        {adTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label} - {type.price}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-muted-foreground">Monthly Budget Range</label>
                      <select
                        name="budget_range"
                        value={formData.budget_range}
                        onChange={handleChange}
                        className="input-glass w-full h-12"
                        data-testid="ad-budget"
                      >
                        <option value="">Select Budget Range</option>
                        <option value="under_5000">Under KES Custom Price</option>
                        <option value="5000_10000">KES Custom Price</option>
                        <option value="10000_25000">KES Custom Price</option>
                        <option value="25000_50000">KES Custom Price</option>
                        <option value="above_50000">Above Custom Price</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-muted-foreground">Tell Us About Your Business</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="input-glass w-full py-3 resize-none"
                        rows={4}
                        placeholder="Describe your business and what you'd like to advertise..."
                        required
                        data-testid="ad-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full btn-primary h-14 text-lg"
                      disabled={loading}
                      data-testid="ad-submit"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          Submit Inquiry
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground">
            For direct inquiries, email us at{' '}
            <a href="mailto:info.akit001@gmail.com" className="text-primary hover:underline">
              info.akit001@gmail.com
            </a>
          </p>
        </div>

        {/* Web Developer Ad Section */}
        <DevAdSection />
      </div>
    </div>
  );
};
